import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import CustomNavbar from "../CustomPermissions/CustomNavbar";

import { getApiUrl, API_ENDPOINTS, API_BASE_URL } from "../../config/api";

function ShowTeamLeader({ prop }) {
  const location = useLocation();
  const stateValue = location.state && location.state["newUser"];

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [deleteConfirmLeader, setDeleteConfirmLeader] = useState(null);
  const [editLeader, setEditLeader] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDepartment, setEditDepartment] = useState('');
  const [editImage, setEditImage] = useState(null);
  const [departments, setDepartments] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchLeaders() {
      try {
        const response = await fetch(
          getApiUrl("/LeadersTable/leaders/", false),
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
          }
        );
        if (!response.ok) {
          throw new Error("Failed to fetch leaders");
        }
        const data = await response.json();
        setData(data.leaders);
        setIsSuperuser(data.is_superuser || false);
        setLoading(false);
      } catch (error) {
        console.error(error);
        setLoading(false);
      }
    }
    fetchLeaders();
  }, []);

  // Fetch departments for edit form
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const accessToken = localStorage.getItem("access_token");
        if (accessToken) {
          const response = await axios.get(
            getApiUrl('/LeadersTable/teamleader-types/'),
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );
          setDepartments(response.data || []);
        }
      } catch (error) {
        console.error("Error fetching departments:", error);
      }
    };
    fetchDepartments();
  }, []);

  // Handle delete leader
  const handleDeleteLeader = async (leaderId) => {
    try {
      const accessToken = localStorage.getItem("access_token");
      const response = await axios.delete(
        getApiUrl(`/LeadersTable/delete_leader/${leaderId}/`),
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      
      // Refresh the data after deletion
      window.location.reload();
    } catch (error) {
      console.error("Error deleting leader:", error);
      alert(error.response?.data?.message || "Error deleting leader. Please try again.");
    }
    setDeleteConfirmLeader(null);
  };

  // Open edit leader modal
  const openEditLeaderModal = (leaderId, leaderName, leaderDept) => {
    setEditLeader({ id: leaderId, name: leaderName });
    setEditName(leaderName || '');
    setEditDepartment(leaderDept || '');
    setEditImage(null);
  };

  // Handle edit leader details
  const handleEditLeader = async () => {
    try {
      const accessToken = localStorage.getItem("access_token");
      const formData = new FormData();
      
      if (editName) formData.append('name', editName);
      if (editDepartment) formData.append('dept', editDepartment);
      if (editImage) formData.append('image', editImage);
      
      const response = await axios.put(
        getApiUrl(`/LeadersTable/update_leader/${editLeader.id}/`),
        formData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'multipart/form-data'
          },
        }
      );
      
      alert(response.data.message || 'Leader details updated successfully!');
      setEditLeader(null);
      // Refresh the data after update
      window.location.reload();
    } catch (error) {
      console.error("Error updating leader details:", error);
      alert(error.response?.data?.message || "Error updating leader details. Please try again.");
    }
  };

  return (
    <div className="flex">
      <CustomNavbar className="bg-blue-500 p-4 text-white" />
      <section
        className="flex-grow overflow-auto"
        style={{ width: "100%", auto: "auto" }}
      >
        <div className="bg-white shadow-md rounded-lg p-4">
          {loading ? (
            <div className="text-center py-6 text-blue-500 font-semibold">
              Loading...
            </div>
          ) : (
            <>
              <h2 className="text-3xl font-bold text-blue-500 mb-3">
                Team Leaders
              </h2>
              <table className="w-full border-collapse">
                <thead>
                  <tr
                    className="bg-blue-500 text-white"
                    style={{ fontSize: "16px" }}
                  >
                    <th className="p-2 w-5">#</th>
                    <th className="p-2 w-10">Image</th>
                    <th className="p-2 w-12">Name</th>
                    <th className="p-2 w-48">Percentage</th>
                    <th className="p-2 w-15">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {data &&
                    data.map((record, index) => (
                      <tr
                        key={record.id}
                        className="border-b"
                        style={{ fontSize: "14px" }}
                      >
                        <td className="p-2 text-center">{index + 1}</td>
                        <td className="p-2">
                          <img
                            src={`${API_BASE_URL}${record.image}`}
                            // className="h-20 w-16 rounded-full object-cover"
                            style={{ width: "200px", height: "120px" }}
                            alt={record.name}
                          />
                        </td>
                        <td className="p-2 text-center">{record.name}</td>
                        <td className="p-2 text-center">
                          <div className="relative w-full h-6 bg-gray-200 rounded-md overflow-hidden">
                            <div
                              className={`h-full mb-2  ${
                                record.calculatePercentage >= 100
                                  ? "bg-blue-600"
                                  : record.calculatePercentage >= 95
                                  ? "bg-green-500"
                                  : record.calculatePercentage >= 85
                                  ? "bg-yellow-500"
                                  : "bg-red-500"
                              }`}
                              style={{
                                width: `${Math.min(record.calculatePercentage, 100)}%`,
                              }}
                            ></div>
                          </div>
                          <span 
                            className={`text-sm font-semibold ${
                              record.calculatePercentage >= 100
                                ? "text-blue-600"
                                : record.calculatePercentage >= 95
                                ? "text-green-500"
                                : record.calculatePercentage >= 85
                                ? "text-yellow-500"
                                : "text-red-500"
                            }`}
                          >
                            {Math.ceil(record.calculatePercentage)}%
                          </span>
                        </td>
                        <td
                          className="mt-3 text-center "
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            padding: "30px",
                            justifyContent: "space-between",
                          }}
                        >
                          <Link
                            to={`/currentmonthTaskForm/${record.id}`}
                            className="p-2 text-white rounded"
                            style={{ backgroundColor: "#0BD953" }}
                            title="Add"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="currentColor"
                              className="bi bi-plus-lg"
                              color="#fff"
                              viewBox="0 0 16 16"
                              
                            >
                              <path
                                fillRule="evenodd"
                                d="M8 2a.5.5 0 0 1 .5.5v5h5a.5.5 0 0 1 0 1h-5v5a.5.5 0 0 1-1 0v-5h-5a.5.5 0 0 1 0-1h5v-5A.5.5 0 0 1 8 2"
                              />
                            </svg>
                          </Link>

                          <Link
                            to={`/leadersTask/${record.id}`}
                            className="p-2 text-white rounded"
                            style={{ backgroundColor: "#4472ca" }}
                            title="View"

                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="currentColor"
                              className="bi bi-eye"
                              viewBox="0 0 16 16"

                            >
                              <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8M1.173 8a13 13 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5s3.879 1.168 5.168 2.457A13 13 0 0 1 14.828 8q-.086.13-.195.288c-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5s-3.879-1.168-5.168-2.457A13 13 0 0 1 1.172 8z" />
                              <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5M4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0" />
                            </svg>
                          </Link>

                          <Link
                            to={`/leadersOldRecords/${record.id}`}
                            className="p-2 text-white rounded"
                            style={{ backgroundColor: "orange" }}
                            title="Old Month Records"

                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="currentColor"
                              className="bi bi-graph-up-arrow"
                              viewBox="0 0 16 16"
                            >
                              <path
                                fillRule="evenodd"
                                d="M0 0h1v15h15v1H0zm10 3.5a.5.5 0 0 1 .5-.5h4a.5.5 0 0 1 .5.5v4a.5.5 0 0 1-1 0V4.9l-3.613 4.417a.5.5 0 0 1-.74.037L7.06 6.767l-3.656 5.027a.5.5 0 0 1-.808-.588l4-5.5a.5.5 0 0 1 .758-.06l2.609 2.61L13.445 4H10.5a.5.5 0 0 1-.5-.5"
                              />
                            </svg>
                          </Link>

                          {isSuperuser && (
                            <>
                              <button
                                onClick={() => openEditLeaderModal(record.id, record.name, record.dept)}
                                className="p-2 text-white rounded"
                                style={{ backgroundColor: "#10b981" }}
                                title="Edit Leader"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  fill="currentColor"
                                  className="bi bi-pencil"
                                  viewBox="0 0 16 16"
                                >
                                  <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325"/>
                                </svg>
                              </button>
                              <button
                                onClick={() => setDeleteConfirmLeader({ id: record.id, name: record.name })}
                                className="p-2 text-white rounded"
                                style={{ backgroundColor: "#ef4444" }}
                                title="Delete Leader"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  fill="currentColor"
                                  className="bi bi-trash"
                                  viewBox="0 0 16 16"
                                >
                                  <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z"/>
                                  <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4zM2.5 3h11V2h-11z"/>
                                </svg>
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </>
          )}
        </div>

        {/* Edit Leader Modal */}
        {editLeader && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-xl font-bold text-green-600 mb-4">
                📝 Edit Leader Details - {editLeader.name}
              </h3>
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name:
                  </label>
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Enter leader name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Department:
                  </label>
                  <select
                    value={editDepartment}
                    onChange={(e) => setEditDepartment(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="">Select Department</option>
                    {departments.map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Profile Image:
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setEditImage(e.target.files[0])}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave empty to keep current image</p>
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setEditLeader(null)}
                  className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditLeader}
                  className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded transition-colors"
                  disabled={!editName}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirmLeader && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-xl font-bold text-red-600 mb-4">
                ⚠️ Confirm Deletion
              </h3>
              <p className="text-gray-700 mb-6">
                Are you sure you want to delete leader <strong>{deleteConfirmLeader.name}</strong>?
                <br />
                <span className="text-red-500 text-sm">
                  This action cannot be undone. All associated data will be permanently deleted.
                </span>
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDeleteConfirmLeader(null)}
                  className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteLeader(deleteConfirmLeader.id)}
                  className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
export default ShowTeamLeader;
